import clsx from 'clsx';
import { useId } from 'react';
import { BiPaperclip, BiX } from 'react-icons/bi';

import { LoaderSpinner } from '../LoaderSpinner/LoaderSpinner';

import s from './FilePicker.module.scss';

export type FilePickerProps = {
  /** The file already chosen — its name replaces the placeholder. */
  file?: File | null;
  /**
   * A file held elsewhere — already uploaded, so there is no `File` to name it. Shown
   * like a chosen file; a picked `file` takes precedence.
   */
  fileName?: string;
  /**
   * Draws the picker as a form field — the label above, the inputs' box and height —
   * so it lines up with the `Input`s and `Autocomplete`s beside it in a row.
   */
  label?: string;
  /** Puts a cross on a chosen file; pressing it hands the choice back. */
  onClear?: () => void;
  /** What the picker says before anything is chosen. */
  placeholder?: string;
  /** MIME types, as the input's own `accept`: `image/*`, `application/pdf`. Any file when omitted. */
  accept?: string;
  onPick: (file: File) => void;
  /** Under the picker, where a form's validation lands. */
  error?: string;
  /** The picked file is being uploaded or read — a spinner takes the right edge and picking is off. */
  loading?: boolean;
  /** The picker's box only — width, margin, grid placement. */
  className?: string;
};

/**
 * The upload modals' file selector: one bordered line that names the file, and
 * opens the system picker when pressed.
 *
 * Moved here from the Library's upload modal, where it was page styling, so the
 * gallery's upload could be the same control rather than a look-alike. Distinct
 * from `PdfDropzone` in `ProfileDoc`, the tall drop zone of a profile document:
 * this one sits in a form among other fields and is a field's height.
 *
 * A label around a real input, so pressing any of it opens the picker; the input
 * is visually hidden rather than `display: none`, so it still takes focus. No
 * focus ring: the modals it sits in clip overflow, which cut the ring off.
 *
 * @alias file input, file selector, upload file, choose file, attach file
 */
export const FilePicker = ({
  file,
  fileName,
  label,
  onClear,
  placeholder = 'Upload File',
  accept,
  onPick,
  error,
  loading = false,
  className,
}: FilePickerProps) => {
  const errorId = useId();
  const labelId = useId();
  const chosen = file?.name ?? fileName;

  return (
    <div className={clsx(s.field, label && s.withLabel, className)}>
      {label && (
        <span id={labelId} className={s.label}>
          {label}
        </span>
      )}
      <div className={s.box}>
        <label className={clsx(s.picker, label && s.asField, loading && s.loading)}>
          <span className={clsx(s.name, !chosen && label && s.placeholder)}>
            {chosen ?? placeholder}
          </span>
          {label && !loading && !(chosen && onClear) && (
            <BiPaperclip className={s.icon} size={20} aria-hidden="true" />
          )}
          <input
            type="file"
            accept={accept}
            className={s.input}
            disabled={loading}
            aria-busy={loading}
            aria-labelledby={label ? labelId : undefined}
            aria-invalid={!!error}
            aria-describedby={error ? errorId : undefined}
            onChange={(e) => {
              const picked = e.target.files?.[0];
              if (picked) onPick(picked);
              // Cleared so picking the same file twice in a row still fires a change.
              e.target.value = '';
            }}
          />
        </label>
        {/* Beside the label rather than in it: inside, a press would open the picker too. */}
        {loading && <LoaderSpinner className={s.spinner} size={24} fullHeight={false} label="" />}
        {chosen && onClear && !loading && (
          <button type="button" className={s.clear} aria-label="Remove File" onClick={onClear}>
            <BiX size={20} aria-hidden="true" />
          </button>
        )}
      </div>
      {error && (
        <p id={errorId} className={s.error}>
          {error}
        </p>
      )}
    </div>
  );
};

export default FilePicker;
